import { erc20Abi } from 'viem';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';
import { QueryOptions } from 'utils/useQueryUtils';
import { useQuery } from '@tanstack/react-query';
import { getPublicClient } from '@wagmi/core';
import { wagmiConfig } from 'structure/providers/wagmiConfig';

export interface UseTokenListInfoParams extends QueryOptions {
  tokenList: `0x${string}`[];
  chainId?: number;
}

interface Token {
  symbol: string;
  name: string;
  decimals: number;
  address: `0x${string}`;
  chainId: number;
}

export interface TokenListInfo {
  list: Token[];
  map: Record<string, Token>;
}

export const useTokenListInfo = (params: UseTokenListInfoParams) => {
  const { tokenList, chainId, query } = params;

  const useQueryOptions = {
    queryKey: queryKeys.tokenListInfo({ chainId, tokenList }),
    ...query,
  };

  const publicClient = getPublicClient(wagmiConfig, { chainId });

  return useQuery({
    ...useQueryOptions,
    queryFn: async (): Promise<TokenListInfo> => {
      if (!publicClient) {
        throw new Error('No public client found');
      }

      const vaultDataCalls = tokenList
        .map((token) => [
          {
            address: token,
            abi: erc20Abi,
            functionName: 'symbol',
            authorizationList: undefined,
          },
          {
            address: token,
            abi: erc20Abi,
            functionName: 'decimals',
            authorizationList: undefined,
          },
          {
            address: token,
            abi: erc20Abi,
            functionName: 'name',
            authorizationList: undefined,
          },
        ])
        .flat();

      const methodNumber = vaultDataCalls.length / tokenList.length;

      const tokenDetails = await publicClient
        .multicall({
          contracts: vaultDataCalls,
          authorizationList: undefined,
        })
        .catch((error) => {
          console.error('Multicall for vault data failed:', error);
          return [];
        });

      const tokenInfoList: Token[] = [];
      for (let i = 0; i < tokenList.length; i++) {
        const offsetIdx = i * methodNumber;
        const symbol = (tokenDetails[offsetIdx]?.result as string) || '';
        const decimals = (tokenDetails[offsetIdx + 1]?.result as number) || 0;
        const name = (tokenDetails[offsetIdx + 2]?.result as string) || '';
        const address = tokenList[i];

        tokenInfoList.push({
          symbol,
          decimals,
          name,
          address,
          chainId: chainId ?? 0,
        });
      }

      const map = tokenInfoList.reduce(
        (acc, token) => {
          acc[token.address] = token;
          return acc;
        },
        {} as Record<string, Token>
      );

      return { list: tokenInfoList, map };
    },
  });
};
